/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faDollar,
  faPlus,
  faTrash,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { optionTemplate } from '@/layout/Header/Header';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Depo20FormData, EstadoType, DocumentTableData } from '@/types/depo20';
import classNames from 'classnames';
import { Button, Container, DropdownField, LoadingSpinner } from '@/components';
import { useToastNotification } from '@/hooks';
import { queryClient } from '@/providers/AppProvider';

import { PagesPaths } from '@/enums';
import Depo20Table from './Depo20Table';
import { getDepo20List } from './services/getDepo20List';
import styles from './Depo20.module.scss';
import Depo20ClientForm, { Depo20FormState } from './Depo20ClientForm';
import './Depo20.scss';
import { getDepo20TableList } from './services/getDepo20TableList';
import ModalDetailsAddProforma from './components/ModalDetailsAddProforma';
import ModalDetailPayment from './components/ModalDetailPayment';
import { deleteProforma } from './services/deleteProforma';
import { addLiquidacion } from './services/addLiquidacion';
import { deletedImportFCL } from './services/deletedImportFCL';
import { notDeletableStates } from '../ImportFCL/ImportFCL';
import { titularRut } from '../CustomerRegistration/titularRut';
import { verifyRut } from '../CustomerRegistration/verifyRut';
import { comprobarDocumento } from './services/comprobarDocumento';

interface IDepo20Props {
  tipo: 'DEPO20' | 'ServicioAdicional';
}

interface LiquidacionData {
  nombre: string;
  rut: string;
  referencia: string | undefined;
}

const Depo20: React.FC<IDepo20Props> = ({ tipo }) => {
  const { t } = useTranslation('depo20');
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = parseInt(queryParams.get('id') ?? '0', 10);
  const [selectedItems, setSelectedItems] = useState<DocumentTableData[]>([]);
  const { showToast } = useToastNotification();

  const pathSegments = location.pathname.split('/');
  const tipoLiquidacion =
    pathSegments[3] === 'servicio-adicional' ? 'Proforma' : 'DEPO20';

  const [isAddProformaDialogShown, setIsAddProformaDialogShown] =
    useState(false);

  const [isPaymentDialogShown, setIsPaymentDialogShown] = useState(false);

  const { data: formClientData, isLoading: formDataLoading } =
    useQuery<Depo20FormData>({
      queryKey: ['depo20Client', id],
      queryFn: () => getDepo20List(id),
    });

  const {
    mutate: mutateVerifyRut,
    isPending: rutLoading,
    data: rutFieldFormData,
  } = useMutation({
    mutationFn: verifyRut,
  });

  const [formDataValues, setFormDataValues] = useState<Depo20FormState>(() => {
    const initialState: Depo20FormState = {
      rut:
        formClientData?.rutCliente ??
        window.localStorage.getItem(
          `rutFavoritoLiquidacion ${formClientData?.oid}`
        ) ??
        null,
      nombre:
        formClientData?.cliente ??
        window.localStorage.getItem(
          `nombreFavoritoLiquidacion ${formClientData?.oid}`
        ) ??
        null,
      referencia: formClientData?.observaciones ?? '',
    };
    return initialState;
  });

  const { mutate: fetchAddLiquidacion, isPending: fetchAddLiquidacionLoading } =
    useMutation({
      mutationKey: ['fetchAddLiquidacion', id],
      mutationFn: (data: LiquidacionData) =>
        addLiquidacion({
          oid: id ?? 0,
          consumidorFinal: false,
          tipo: tipoLiquidacion,
          observaciones: data?.referencia ?? '',
          cliente: !id ? data?.nombre : formDataValues?.nombre ?? '',
          rutCliente: data?.rut ?? '',
          fechaInicio: formClientData
            ? formClientData.fechaInicio
            : new Date().toISOString(),
          despachante: '',
          rutDespachante: '',
        }),
      onSuccess: (response: number) => {
        const newPath = `${location.pathname}?id=${response}`;
        navigate(newPath, { replace: true });
        // showToast({
        //   severity: 'success',
        //   detail: `${t('liquidation')} ${response} ${id ? t('updatedCorrectly') : t('addedCorrectly')}`,
        // });
      },
    });

  const { mutate: mutateTitularRut, isPending: rutTitularLoading } =
    useMutation({
      mutationFn: titularRut,
      onSuccess: (response) => {
        if (Object.keys(response).length) {
          fetchAddLiquidacion({
            nombre: response?.nombre,
            rut: response?.ruc,
            referencia: formDataValues?.referencia,
          });
        } else {
          mutateVerifyRut({ rut: formDataValues.rut ?? '' });
        }
      },
      onError: () => {
        mutateVerifyRut({ rut: formDataValues.rut ?? '' });
      },
    });

  const { mutate: deleteSettlement, isPending: deletingSettlement } =
    useMutation({
      mutationFn: deletedImportFCL,
      onSuccess: () => {
        showToast({
          severity: 'success',
          detail: t('settlementDeleted'),
        });
        queryClient.invalidateQueries({
          queryKey: ['depo20Client', id],
        });
        navigate(PagesPaths.mySettlement);
      },
      onError: () => {
        showToast({
          severity: 'error',
          detail: t('settlementNotDeleted'),
        });
      },
    });

  const {
    mutate: refetchTableData,
    isPending: refetchTableDataLoading,
    data: refetchedTableData,
  } = useMutation<DocumentTableData[]>({
    mutationKey: ['refetchTableData', id],
    mutationFn: () => getDepo20TableList(id),
  });

  const { data: tableData, isLoading: tableDataLoading } = useQuery<
    DocumentTableData[]
  >({
    queryKey: ['depo20Table', id],
    queryFn: () => getDepo20TableList(id),
    enabled: !!id,
  });

  useEffect(() => {
    const checkAndDeleteSettlement = async () => {
      if (
        !formDataLoading &&
        !tableDataLoading &&
        id &&
        !isAddProformaDialogShown
      ) {
        await queryClient.invalidateQueries({ queryKey: ['depo20Table', id] });
        const latestTableData = await getDepo20TableList(id);
        if (latestTableData.length === 0) {
          showToast({
            severity: 'warn',
            detail: t('noProformasDeleteWarning'),
          });
          deleteSettlement({ oidLiquidacion: id });
        }
      }
    };

    checkAndDeleteSettlement();
  }, [
    tableData,
    id,
    formDataLoading,
    tableDataLoading,
    deleteSettlement,
    showToast,
    t,
    isAddProformaDialogShown,
  ]);

  const saveLiquidacion = () => {
    if (
      id &&
      id !== 0 &&
      (!tableData || tableData.length === 0) &&
      !isAddProformaDialogShown &&
      !fetchAddLiquidacionLoading &&
      !rutTitularLoading &&
      !rutLoading
    ) {
      showToast({
        severity: 'warn',
        detail: t('noProformasDeleteWarning'),
      });
      deleteSettlement({ oidLiquidacion: id });
      return;
    }

    if (
      (!tableData || tableData.length === 0) &&
      !id &&
      !isAddProformaDialogShown
    ) {
      showToast({
        severity: 'error',
        detail: t('noProformasError'),
      });
      return;
    }

    mutateTitularRut({ rut: formDataValues.rut ?? '' });
  };

  const toggleAddProformaDialog = (refetch?: boolean) => {
    setSelectedItems([]);

    if (!id && !isAddProformaDialogShown && formDataValues.rut) {
      mutateTitularRut({ rut: formDataValues.rut });
    }

    setIsAddProformaDialogShown(!isAddProformaDialogShown);

    if (refetch) {
      refetchTableData();
    }
  };

  const toggleIsPaymentDialog = () => {
    setSelectedItems([]);
    setIsPaymentDialogShown(!isPaymentDialogShown);
  };

  const handleSelectionChange = (e: { value: any }) => {
    setSelectedItems(e.value);
  };

  const { mutate: fetchDeleteProformaData, isPending: deleteProformaLoading } =
    useMutation({
      mutationKey: ['deleteProforma', id],
      mutationFn: () =>
        deleteProforma({
          oidLiquidacionWeb: id,
          oidProForma: selectedItems.map((item) => item.oid),
        }),
      onError: () => {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      },
      onSuccess: () => {
        refetchTableData();
        queryClient.invalidateQueries({ queryKey: ['depo20Table', id] });
        showToast({
          severity: 'success',
          detail: t('proformDeleted', { count: selectedItems.length }),
        });
        setSelectedItems([]);
      },
    });

  const statusColors: { [key in EstadoType]: string } = {
    'En Proceso': '#374f93',
    'En Edición': '#ffd389',
    'Pendiente de Pago': '#fd8800',
    Agendada: '#4780bf',
    Paga: '#619b77',
    Solicitada: '#91c4fc',
    Validada: '#cbfcde',
    Rechazada: '#e24b39',
    Autorizada: '#7dd19d',
    defaultColor: '#ffd389',
  };

  const getStatusColor = (estado?: EstadoType): string => {
    return estado ? statusColors[estado] : 'defaultColor';
  };

  const options = [
    {
      name: t('delete'),
      value: 'delete',
      icon: faTrash,
      color: 'danger',
    },
  ];

  const handleDropdownChange = (e: { value: string | null }, name: string) => {
    if (e.value === 'print' && name === 'userDropdown') {
      navigate(PagesPaths.Settings);
    }
    if (e.value === 'delete') {
      deleteSettlement({ oidLiquidacion: id || 0 });
    }
    return null;
  };

  const handleDeleteProforma = () => {
    fetchDeleteProformaData();
  };

  const handleAddProformaClick = async () => {
    if (!formDataValues.rut) {
      showToast({
        severity: 'error',
        detail: t('rutEmptyError'),
      });
      return;
    }

    if (!(formDataValues.rut?.length === 12 && formDataValues.nombre?.trim())) {
      if (formDataValues.rut?.length !== 12) {
        showToast({
          severity: 'error',
          detail: t('rutInvalidError'),
        });
        return;
      }
      if (!formDataValues.nombre?.trim()) {
        showToast({
          severity: 'error',
          detail: t('nameRequiredError'),
        });
        return;
      }
    }

    const response = await comprobarDocumento({
      documento: formDataValues.rut ? formDataValues.rut : '',
      tipo: 2,
    });

    if (response === 1) {
      showToast({
        severity: 'error',
        detail:
          'El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información',
      });
      return;
    }

    if (response === 2) {
      showToast({
        severity: 'error',
        detail: 'El RUT ingresado es inválido',
      });
      return;
    }

    toggleAddProformaDialog();
  };

  useEffect(() => {
    if (rutFieldFormData?.m_Item1) {
      setFormDataValues({
        nombre:
          rutFieldFormData?.m_Item1?.razonSocial ??
          rutFieldFormData?.m_Item1?.nombreFantasia,
      });
    }
    if (rutFieldFormData?.m_Item1 === null && rutFieldFormData?.m_Item2) {
      showToast({
        severity: 'error',
        detail: rutFieldFormData?.m_Item2,
      });
    }
  }, [rutFieldFormData, showToast]);

  useEffect(() => {
    if (formClientData && Object.keys(formClientData).length) {
      setFormDataValues({
        nombre: formClientData?.cliente,
        rut: formClientData?.rutCliente,
        referencia: formClientData?.observaciones,
      });
    }
  }, [formClientData]);

  useEffect(() => {
    const noAccesibleStates = ['Paga', 'Pendiente de Pago'];

    if (noAccesibleStates.includes(formClientData?.estado || '')) {
      navigate(PagesPaths.mySettlement);
    }
  }, [formClientData, navigate]);

  return (
    <Container className={styles.containerList}>
      <ModalDetailsAddProforma
        oidLiquidacion={id}
        isDialogShown={isAddProformaDialogShown}
        toggleDialog={toggleAddProformaDialog}
        tipoLiquidacion={tipoLiquidacion}
      />
      {formClientData && isPaymentDialogShown && (
        <ModalDetailPayment
          oid={id}
          isDialogShown={isPaymentDialogShown}
          toggleDialog={toggleIsPaymentDialog}
          formClientData={formClientData}
          fetchAddLiquidacionLoading={fetchAddLiquidacionLoading}
          saveLiquidacion={saveLiquidacion}
          formDataValues={formDataValues}
          tipoLiquidacion={tipoLiquidacion}
        />
      )}
      <div className="flex flex-column md:flex-row justify-between align-items-center border-b border-gray-300">
        <div className="col-12 md:col-9">
          <Link
            to={PagesPaths.mySettlement}
            className={`${styles.breadcrumb} hover:text-blue-800 w-100`}
          >
            <FontAwesomeIcon icon={faChevronLeft} />

            {t('backToSettlements')}
          </Link>
          {/* {id && <span className={`${styles.breadcrumb} ml-2`}>/ {id}</span>} */}

          <h1 className={classNames('mt-2 mb-0', styles.title)}>
            {tipo === 'DEPO20' &&
              (!id ? t('newSettlementTitle') : t('depo20WithId'))}

            {tipo === 'ServicioAdicional' &&
              (!id ? t('newAdditionalService') : t('additionalServiceWithId'))}
          </h1>
        </div>
        <div className="col-12 md:col-3 flex gap-3 justify-content-end items-start">
          <div className="flex flex-wrap gap-3">
            <Button
              className={`${styles.paymentButton} w-auto px-3 gap-2`}
              onClick={toggleIsPaymentDialog}
              disabled={
                (!tableData || tableData.length === 0) &&
                (!refetchedTableData || refetchedTableData.length === 0)
              }
            >
              <FontAwesomeIcon icon={faDollar} />
              {t('paymentButton')}
            </Button>
          </div>
          <div className={styles.user}>
            <DropdownField
              id="userDropdown"
              options={options}
              onChange={(e) => handleDropdownChange(e, 'userDropdown')}
              className="Depo20DropdownField "
              itemTemplate={optionTemplate}
              isLoading={deletingSettlement}
              disabled={notDeletableStates.includes(
                formClientData?.estado ?? ''
              )}
            />
          </div>
        </div>
      </div>
      <div
        className={`${styles.status} flex align-items-center justify-content-end flex-row`}
      >
        <span className={styles.bgStatus}>
          <span
            className={styles.ball}
            style={{
              backgroundColor: getStatusColor(
                formClientData?.estado ?? 'defaultColor'
              ),
            }}
          />
          {formClientData?.estado ?? 'En edición'}
        </span>
      </div>
      <div className="flex flex-column md:flex-row align-items-start">
        <section className={classNames('col-12 p-0 md:col-4')}>
          <header className="flex justify-content-between align-items-center py-2">
            <h2 className="text-xl font-semibold text-blue-800  mt-3 sm:mt-0">
              {t('clientInformation')}
            </h2>
            <button
              type="button"
              className={classNames(
                styles.secondaryActionsButtons,
                'mt-3 sm:mt-0'
              )}
              onClick={() => navigate(PagesPaths.CustomerRegistration)}
            >
              <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
              {t('createNewClient')}
            </button>
          </header>
          <div>
            <Depo20ClientForm
              formClientData={formClientData}
              formDataLoading={
                formDataLoading || rutTitularLoading || rutLoading
              }
              fetchAddLiquidacionLoading={fetchAddLiquidacionLoading}
              saveLiquidacion={saveLiquidacion}
              formDataValue={formDataValues}
              setFormDataValues={setFormDataValues}
            />
          </div>
        </section>

        <section className={`${styles.tableSection} col-12 md:col-8`}>
          <header className="flex justify-between items-center py-2">
            <div className="flex justify-content-between flex-column md:flex-row md:align-items-center w-full">
              <h2 className="text-xl font-semibold text-blue-800">
                {t('proformaSectionTitle')}
              </h2>
              <p className="text-blue-800 font-14">
                {tableData?.length} {t('proformasAddedInfo')}
              </p>
            </div>
            <Button
              className={`${styles.addProformaButton} mb-4 mt-5`}
              onClick={handleAddProformaClick}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t('addProformaButton')}
            </Button>
          </header>
          <div>
            <div
              className={classNames(
                'p-3 rounded-t-md',
                styles.secondaryActionsWrapper
              )}
            >
              <div>
                {/* <button
                  type="button"
                  className={classNames(styles.secondaryActionsButtons)}
                >
                  <FontAwesomeIcon icon={faPrint} className="mr-2" />
                  <span>{t('printProformaButton')}</span>
                </button> */}
                <Button
                  className={classNames(styles.secondaryActionsButtons, '')}
                  onClick={handleDeleteProforma}
                  disabled={selectedItems?.length === 0}
                >
                  {deleteProformaLoading && <LoadingSpinner size="tiny" />}
                  {!deleteProformaLoading && (
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  )}
                  <span>{t('removeProformaButton')}</span>
                </Button>
              </div>
              <p className={classNames(styles.containersSelected)}>
                {selectedItems?.length
                  ? `${selectedItems?.length} ${t('containersSelected')}`
                  : t('noContainerSelected')}
              </p>
            </div>
            <Depo20Table
              selectedItems={selectedItems}
              handleSelectionChange={handleSelectionChange}
              data={refetchedTableData ?? tableData ?? undefined}
              isLoading={tableDataLoading || refetchTableDataLoading}
              fechaLiquidacion={formClientData?.fechaInicio}
            />
          </div>
        </section>
      </div>
    </Container>
  );
};

export default Depo20;
